import { FC, useRef, useEffect } from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import {
    PdpRespProductTileCarousel,
    PdpRespProductTileCarouselProps,
} from './PdpRespProductTileCarousel/PdpRespProductTileCarousel';
import { visuallySimilar } from '../pdpMessages';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';

// Helpers
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    trackSimilarItemClick,
    trackCarouselItemImpressions,
    VISUALLY_SIMILAR_ITEMS_LIST,
    trackViewMoreVisuallySimilarClick,
} from '../tracking/similarItemsTracking';

import { PdpRespSimilarItemsCarousel_viewer$key } from './__generated__/PdpRespSimilarItemsCarousel_viewer.graphql';
import { PdpRespSimilarItemsCarousel_item$key } from './__generated__/PdpRespSimilarItemsCarousel_item.graphql';
import { PdpRespSimilarItemsCarousel_user$key } from './__generated__/PdpRespSimilarItemsCarousel_user.graphql';

type Props = {
    viewer: PdpRespSimilarItemsCarousel_viewer$key;
    item: PdpRespSimilarItemsCarousel_item$key;
    user: PdpRespSimilarItemsCarousel_user$key;
    currency: string;
};

const viewerRefetchableFragment = graphql`
    fragment PdpRespSimilarItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpRespSimilarItemsCarouselRefetchQuery")
    @argumentDefinitions(fetchVisuallySimilar: { type: "Boolean", defaultValue: false }) {
        ...PdpRespProductTileCarousel_viewer
        visuallySimilarItems: visualSearch(itemPk: $itemId, first: 5)
            @include(if: $fetchVisuallySimilar) {
            edges {
                node {
                    serviceId
                    ...PdpRespProductTileCarousel_items @arguments(priceBookName: $priceBookName)
                }
            }
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespSimilarItemsCarousel_user on User {
        ...PdpRespProductTileCarousel_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarItemsCarousel_item on Item {
        serviceId
        isArt
        isSold
        isUnpurchasable
    }
`;

export const PdpRespSimilarItemsCarousel: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
}) => {
    const [viewer, viewerRefetch] = useRefetchableFragment(viewerRefetchableFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const impressionTrackingRef = useRef({});
    const { visuallySimilarItems: visuallySimilarItemNodes } = viewer;
    const { serviceId: itemId, isArt, isSold, isUnpurchasable } = item;
    const isUnavailable = isSold || isUnpurchasable;
    const visuallySimilarItems = flattenConnectionNodes(visuallySimilarItemNodes);
    const isClient = useClientState();
    let trackingLabel: string;
    const carouselProps: Omit<
        PdpRespProductTileCarouselProps,
        'viewer' | 'user' | 'currency' | 'onItemsVisible' | 'onClickItemTile'
    > = {
        title: '',
        location: '',
        items: [],
    };

    useEffect(() => {
        if (isClient && isArt && !isUnavailable && !visuallySimilarItemNodes) {
            viewerRefetch({
                fetchVisuallySimilar: true,
            });
        }
    }, [isClient, isArt, isUnavailable, visuallySimilarItemNodes, viewerRefetch]);

    if (isArt && visuallySimilarItems.length) {
        carouselProps.title = visuallySimilar;
        carouselProps.items = visuallySimilarItems;
        carouselProps.location = locations.VISUALLY_SIMILAR_ITEM_LIST;
        carouselProps.viewAllLinkData = { path: `/similar/?item=${itemId}`, isLinkable: true };
        carouselProps.onClickViewAll = trackViewMoreVisuallySimilarClick;

        trackingLabel = VISUALLY_SIMILAR_ITEMS_LIST;
    } else {
        return null;
    }

    return (
        <PdpRespProductTileCarousel
            viewer={viewer}
            user={user}
            currency={currency}
            onItemsVisible={(visibleItems, startIndex) => {
                trackCarouselItemImpressions({
                    visibleItems,
                    startIndex,
                    impressionTrackingRef,
                    list: trackingLabel,
                });
            }}
            onClickItemTile={(tileItem, index) => {
                trackSimilarItemClick(index, tileItem, trackingLabel);
            }}
            {...carouselProps}
        />
    );
};
